import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import EditableVATInvoice from './EditableVATInvoice';

const EditInvoice = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [initialData, setInitialData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const apiUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

  // Helper function to format date for HTML input (YYYY-MM-DD)
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toISOString().split('T')[0];
    } catch {
      return '';
    }
  };

  useEffect(() => {
    const fetchInvoice = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(`${apiUrl}/api/invoices/${id}`);
        const invoice = response.data.data || response.data;

        // Transform the invoice data to match the form structure
        const transformedData = {
          id: invoice.id, // Store original invoice ID for update
          invoiceNo: invoice.invoice_no || invoice.invoiceNo || 'INV-001',
          invoiceDate: formatDateForInput(invoice.invoice_date || invoice.date),
          dueDate: formatDateForInput(invoice.due_date || invoice.dueDate),
          mode: invoice.mode || null,
          status: invoice.status || 'Unpaid',
          customerId: invoice.customer_id || invoice.customerId || null,

          // Customer Information
          customerName: invoice.customer?.name || invoice.customerName || '',
          customerTrn: invoice.customer?.trn || invoice.customerTrn || '',
          customerPhone: invoice.customer?.phone || invoice.customerPhone || '',
          customerAddress: invoice.customer?.address || invoice.customerAddress || '',
          customerCity: invoice.customer?.city || invoice.customerCity || '',
          customerCountry: invoice.customer?.country || invoice.customerCountry || 'UAE',

          // Line Items
          items: (invoice.ilines || invoice.items || []).map(line => ({
            description: line.description || '',
            quantity: parseFloat(line.quantity || 0),
            unit: line.unit || 'Unit',
            unit_price: parseFloat(line.unit_price || line.unitPrice || 0),
            discount_amount: parseFloat(line.discount_amount || line.discountAmount || 0),
            vat_percent: parseFloat(line.vat_percent || line.vatPercent || 0),
          })),

          // Notes
          notes: invoice.notes || '',
          
          // Receipts (for determining if payment fields should be locked)
          receipts: invoice.receipts || [],
        };

        setInitialData(transformedData);
      } catch (err) {
        console.error('Error fetching invoice:', err);
        setError('Unable to load invoice. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchInvoice();
    }
  }, [id, apiUrl]);

  const handleEditSubmit = async (invoiceData) => {
    try {
      // Prepare update payload
      const updatePayload = {
        customer_id: invoiceData.customerId,
        invoice_date: invoiceData.invoiceDate,
        due_date: invoiceData.dueDate,
        status: invoiceData.status,
        mode: invoiceData.status === 'Unpaid' ? '' : (invoiceData.mode || ''),
        notes: invoiceData.notes,
        items: invoiceData.items,
      };

      // Send update request to API
      await axios.put(`${apiUrl}/api/invoices/${id}`, updatePayload);

      // Show success message and navigate back
      alert('Invoice updated successfully!');
      navigate('/invoices');
    } catch (err) {
      console.error('Error updating invoice:', err);
      alert('Error updating invoice. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="mt-4 text-gray-600">Loading invoice...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
          <h3 className="text-red-900 font-bold text-lg mb-2">Error</h3>
          <p className="text-red-800 mb-4">{error}</p>
          <button
            onClick={() => navigate('/invoices')}
            className="btn btn-sm btn-error text-white"
          >
            Back to Invoices
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-secondary/5 to-accent/5">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-[#23A19B] rounded-t-2xl p-8 text-white shadow-xl">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-4xl font-bold mb-2">Edit Invoice</h1>
              <p className="text-white/80">Invoice # {initialData?.invoiceNo}</p>
            </div>
            <button
              onClick={() => navigate('/invoices')}
              className="btn btn-ghost text-white hover:bg-white/20"
            >
              ← Back to Invoices
            </button>
          </div>
        </div>

        {/* Form Component */}
        {initialData && (
          <EditableVATInvoice
            initialData={initialData}
            onSubmit={handleEditSubmit}
          />
        )}
      </div>
    </div>
  );
};

export default EditInvoice;
